import type {ReduxStore} from 'src/reducers';

import IndexStore from 'src/stores';
import * as React from 'react';
import {useRoutes, useHistory} from 'src/rerouter';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {useDispatch} from 'react-redux';
import {onEnter, redirect} from './utils';
import {USER_FORM_URL_SEGMENT} from 'src/utils/referrer-portal';
import {
  signOut,
  getUserDetails,
} from 'src/action-creators/referrer-portal/authentication';

import AuthenticationContainer from 'src/components/referrer-portal/authentication/container/container';
import SignInContainer from 'src/components/referrer-portal/authentication/signin/signin-container';
import ForgotPasswordContainer from 'src/components/referrer-portal/authentication/forgot/forgot-container';
import ResetPasswordContainer from 'src/components/referrer-portal/authentication/reset/reset-password';
import SignUpContainer from 'src/components/referrer-portal/authentication/signup/sign-up';
import Dashboard from 'src/components/referrer-portal/dashboard/dashboard';
import Settings from 'src/components/referrer-portal/settings/settings';
import Profile from 'src/components/referrer-portal/settings/profile/profile';
import Notification from 'src/components/referrer-portal/settings/notification/notification';
import MyReferrals from 'src/components/referrer-portal/my-referrals/tracking';
import Earnings from 'src/components/referrer-portal/earnings/earnings';
import Leads from 'src/components/referrer-portal/my-referrals/leads/leads';
import Tracking from 'src/components/referrer-portal/my-referrals/referrals/referrals';
import MyEarnings from 'src/components/referrer-portal/earnings/my-earnings';
import Payments from 'src/components/referrer-portal/earnings/payments';
import Jobs from 'src/components/referrer-portal/jobs/jobs';
import {RouterErrorBoundary} from 'src/components/500/boundary';
import SideNav from 'src/components/referrer-portal/side-nav/side-nav';
import ReferralUserForm from 'src/components/referrer-portal/user-form/form-container';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import NotFound from 'src/components/404/404';
import css from 'src/components/referrer-portal/side-nav/side.css';
import {usePageTitle} from 'src/contexts/page-title-context';


export default function ReferrerPortalRoutes({
  senseStore,
  reduxStore,
}: {
  reduxStore: ReduxStore | null | undefined;
  senseStore: IndexStore;
}) {
  const dispatch = useDispatch();
  const history = useHistory();

  const isUserDataLoading = useAsyncDependency(() => {
    const apis: Array<any> = [];
    if (!history.location.pathname.includes(USER_FORM_URL_SEGMENT)) {
      apis.push(dispatch(getUserDetails(senseStore)));
    }
    return Promise.all(apis);
  }, []);

  const [content] = useRoutes([
    {
      // @ts-expect-error - TS2741 - Property 'senseStore' is missing in type '{ children: ReactNode; }' but required in type 'AuthenticationContainerProps'.
      element: ({children}) => <AuthenticationContainer children={children} />,
      children: [
        {
          path: 'signup',
          element: <SignUpContainer />,
        },
        {
          path: 'signin',
          element: <SignInContainer />,
        },
        {
          path: 'signout',
          element: onEnter(() => void dispatch(signOut(senseStore))),
        },
        {
          path: 'forgot',
          element: <ForgotPasswordContainer />,
        },
        {
          path: 'reset',
          element: <ResetPasswordContainer />,
        },
      ],
    },
    {
      element: ({children, pathname}) =>
        reduxStore?.getState()?.referrerPortal?.isAuthed ? (
          <SideNav key={pathname} children={children} />
        ) : (
          <RequireAuth />
        ),
      children: [
        {
          path: ``,
          exact: true,
          element: redirect({pathname: '/dashboard', query: {}}),
        },
        {
          path: `dashboard`,
          element: <Dashboard />,
        },
        {
          path: `dashboard/setup`,
          // @ts-expect-error - TS2345 - Argument of type '() => void' is not assignable to parameter of type '(arg1: BaseHistory, arg2: Readonly<{ [key: string]: string; }>) => (() => void) | undefined'.
          element: onEnter(() => {
            // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
            dispatch(getUserDetails());
          }, <Dashboard />),
        },
        {
          path: `referrals`,
          element: ({children}) => <MyReferrals children={children} />,
          children: [
            {
              path: ``,
              exact: true,
              element: redirect('/referrals/leads'),
            },
            {
              path: 'leads',
              element: <Leads />,
            },
            {
              path: 'all-referrals',
              element: <Tracking />,
            },
          ],
        },
        {
          path: `jobs`,
          element: <Jobs />,
        },
        {
          path: `earnings`,
          element: ({children}) => <Earnings children={children} />,
          children: [
            {
              path: ``,
              exact: true,
              element: redirect('/earnings/my-earnings'),
            },
            {
              path: 'my-earnings',
              element: <MyEarnings />,
            },
            {
              path: 'payments',
              element: <Payments />,
            },
          ],
        },
        {
          path: `settings`,
          element: ({children}) => <Settings children={children} />,
          children: [
            {
              path: ``,
              exact: true,
              element: redirect('/settings/profile'),
            },
            {
              path: 'profile',
              // @ts-expect-error - TS2345 - Argument of type '() => void' is not assignable to parameter of type '(arg1: BaseHistory, arg2: Readonly<{ [key: string]: string; }>) => (() => void) | undefined'.
              element: onEnter(() => {
                // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
                dispatch(getUserDetails());
              }, <Profile />),
            },
            {
              path: 'preferences',
              element: <Notification />,
            },
          ],
        },
      ],
    },
    {
      path: 'u/:referralLinkId',
      element: <ReferralUserForm />,
    },
    {
      path: '*',
      // NOTE (vishal): if they're not logged in, and they hit a 404, ask them to log in.
      element: reduxStore?.getState()?.referrerPortal?.isAuthed ? (
        <NotFound />
      ) : (
        // @ts-expect-error - TS2322 - Type '{ senseStore: IndexStore; }' is not assignable to type 'IntrinsicAttributes'.
        <RequireAuth senseStore={senseStore} />
      ),
    },
  ]);

  const {
    actions: {setPageTitleProps},
  } = usePageTitle();

  React.useEffect(() => {
    setPageTitleProps({
      pageNameKey: '',
      showBackButton: false,
      tabSlotComponent: null,
      rightSlotComponent: null,
      customPageNameComponent: null,
    });
  }, []);

  if (isUserDataLoading) {
    return (
      <div className={css.loader}>
        <CircularLoader />
      </div>
    );
  }

  return <RouterErrorBoundary>{content}</RouterErrorBoundary>;
}

function RequireAuth(): React.ReactElement<React.ComponentProps<'div'>> {
  const history = useHistory();
  const {location} = history;

  React.useEffect(() => {
    history.replace({
      pathname: '/signin',
      query: {
        ...location.query,
        nextPathname:
          location.pathname === '/' ? '/dashboard' : location.pathname,
        nextSearch: location.search,
      },
    });
  }, []);
  return <div></div>;
}
